import { Component, type ErrorInfo, type ReactNode } from 'react'

interface Props {
  children: ReactNode
  /** Optional fallback renderer. Receives the captured error + a reset
   * function. When omitted, a default panel is shown. */
  fallback?: (error: Error, reset: () => void) => ReactNode
}

interface State {
  error: Error | null
}

/**
 * Catches render-phase exceptions in the subtree below it (so one throwing
 * page doesn't take the whole SPA white), logs them, and renders a fallback
 * panel with Reload / Try again. Async and event-handler errors don't reach
 * an error boundary — those need their own try/catch.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('ErrorBoundary caught render exception:', error, info)
  }

  reset = (): void => {
    this.setState({ error: null })
  }

  override render(): ReactNode {
    const { error } = this.state
    if (!error) return this.props.children

    if (this.props.fallback) {
      return this.props.fallback(error, this.reset)
    }

    return (
      <div
        role="alert"
        data-testid="error-boundary-fallback"
        style={{
          maxWidth: 640,
          margin: '4rem auto',
          padding: '1.5rem',
          border: '1px solid var(--danger-border)',
          borderRadius: 8,
          background: 'var(--danger-soft)',
        }}
      >
        <h2 style={{ marginTop: 0, color: 'var(--danger)' }}>Something broke.</h2>
        <p style={{ marginBottom: '1rem' }}>
          The page hit an unexpected error. Reloading usually clears it; if it
          keeps happening, share the message below with the team.
        </p>
        <details style={{ marginBottom: '1rem' }}>
          <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
            Error details
          </summary>
          <pre
            data-testid="error-boundary-message"
            style={{
              marginTop: '0.5rem',
              padding: '0.75rem',
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 4,
              fontSize: '0.85rem',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {error.message}
          </pre>
        </details>
        {/* The shared button ladder (primary + secondary), so both themes read. */}
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            type="button"
            className="primary"
            onClick={() => window.location.reload()}
            data-testid="error-boundary-reload"
          >
            Reload page
          </button>
          <button type="button" onClick={this.reset} data-testid="error-boundary-retry">
            Try again
          </button>
        </div>
      </div>
    )
  }
}
